import { AddSVGProps } from '@visx/shape/lib/types';
import cx from 'classnames';
import { motion, MotionProps } from 'framer-motion';

interface Point {
    x: number;
    y: number;
}

interface BaseProps {
    /** className to apply to line element. */
    className?: string;
    /** fill color applied to line element. */
    fill?: string;
    /** Starting x,y point of the line. */
    from?: Point;
    /** Ending x,y point of the line. */
    to?: Point;
    /** Whether line to/from transition should be animated */
    isAnimated?: boolean;
}

export type LineProps = Omit<AddSVGProps<BaseProps, SVGLineElement>, 'ref'> &
    MotionProps;

/**
 * Port of visx's Line component
 *
 * ref: https://github.com/airbnb/visx/blob/master/packages/visx-shape/src/shapes/Line.tsx
 */
export default function Line({
    from = { x: 0, y: 0 },
    to = { x: 1, y: 1 },
    fill = 'transparent',
    className,
    isAnimated,
    ...restProps
}: LineProps) {
    const isRectilinear = from.x === to.x || from.y === to.y;

    const pointProps = {
        x1: from.x,
        y1: from.y,
        x2: to.x,
        y2: to.y,
    };

    return (
        <motion.line
            className={cx('visx-line', className)}
            animate={isAnimated ? pointProps : undefined}
            {...(isAnimated ? {} : pointProps)}
            fill={fill}
            shapeRendering={isRectilinear ? 'crispEdges' : 'auto'}
            {...restProps}
        />
    );
}
